import React, { useEffect, useState } from 'react';
import { Collapse, styled } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Colors } from 'library';
import { Alert, FieldGroup, Icon, PillButton, Row, Stack, Text, TextInput, Tooltip } from 'library/ui';
import { BusinessData } from '../business/BusinessModels';
import ProgramTypes from 'parent-portal/BusinessLocation/ProgramTypes';
import { consoleLogInDev } from 'shared';
import { getProgramTypes, IProgramType } from 'api/ProgramTypeApi';
import LocationTypes from 'parent-portal/forms/components/pages/BusinessLocationPage/components/LocationTypes';
import FormSection from './FormSection';

interface BusinessDetailsSectionProps {
    firstName?: string;
    isValidProgramId: boolean;
    state?: string;
    onBusinessDataChange: (data: BusinessData) => void;
    onBusinessErrorChange: (hasError: boolean) => void;
    onNotBusinessClick: () => void;
    expanded: boolean;
    complete: boolean;
    onToggle: () => void;
    onNext: () => void;
}

function getProgramLabel(state?: string) {
    if (state === 'Florida') return 'License Number';
    return 'Provider ID/Program ID/License Number**';
}

const NOT_A_BUSINESS_PATTERNS = ['n/a', 'na', 'none', 'no', '-', 'myself'];

function looksLikePersonName(value: string, firstName?: string) {
    const lower = value.toLowerCase().trim();
    if (NOT_A_BUSINESS_PATTERNS.includes(lower)) return true;
    if (firstName && lower === firstName.toLowerCase().trim()) return true;
    return false;
}

export default function BusinessDetailsSection({
    firstName,
    isValidProgramId,
    state,
    onBusinessDataChange,
    onBusinessErrorChange,
    onNotBusinessClick,
    expanded,
    complete,
    onToggle,
    onNext,
}: BusinessDetailsSectionProps) {
    const [name, setName] = useState<string>();
    const [locationTypes, setLocationTypes] = useState<number[]>([]);
    const [programId, setProgramId] = useState<string>();
    const [nameError, setNameError] = useState(false);
    const [showNotBusinessHint, setShowNotBusinessHint] = useState(false);
    const [programTypeOptions, setProgramTypeOptions] = useState<IProgramType[]>([]);
    const [programTypes, setProgramTypes] = useState<string[]>([]);

    const programLabel = getProgramLabel(state);

    useEffect(() => {
        setNameError(name === '');

        if (!!name && !nameError) {
            const data: BusinessData = {
                name,
                programId,
                programTypes: programTypeOptions.filter((x) => programTypes.includes(x.name)).map((x) => x.id),
                locationTypes,
            };
            onBusinessDataChange(data);
            onBusinessErrorChange(false);
        } else {
            onBusinessErrorChange(true);
        }
    }, [name, nameError, programId, programTypes, locationTypes]);

    useEffect(() => {
        if (!state) {
            if (programTypes.length) setProgramTypes([]);
        } else {
            getProgramTypes(state)
                .then((response) => {
                    setProgramTypeOptions(response);
                    if (!response.length) setProgramTypes([]);
                })
                .catch(consoleLogInDev);
        }
    }, [state]);

    function handleNameChange(value: string) {
        setName(value);
        setShowNotBusinessHint(looksLikePersonName(value, firstName));
    }

    function onProgramTypesChanged(option: string) {
        if (programTypes.includes(option)) {
            setProgramTypes(programTypes.filter((x) => x !== option));
        } else {
            setProgramTypes([...programTypes, option]);
        }
    }

    return (
        <FormSection
            title="Business Details"
            icon={<BusinessIcon />}
            expanded={expanded}
            complete={complete}
            onToggle={onToggle}
            onNext={onNext}
            autoFocusId="txtBusinessName"
        >
            <Stack gap="md" fullWidth>
                <Stack gap="sm">
                    <FieldGroup label="Business Name*">
                        <TextInput
                            id="txtBusinessName"
                            value={name ?? ''}
                            onChange={handleNameChange}
                            error={nameError ? 'Please enter a business name.' : ''}
                            fullWidth
                        />
                    </FieldGroup>
                    <Collapse in={showNotBusinessHint} unmountOnExit>
                        <Alert variant="info">
                            <Row justify="between" align="center" gap="sm">
                                <Text size="xs">
                                    This doesn&apos;t look like a business name. If you&apos;re a teacher looking for
                                    work, select the Teacher card above instead.
                                </Text>
                                <PillButton variant="secondary" onClick={onNotBusinessClick}>
                                    <Text size="xs" weight="bold">
                                        Switch to Teacher
                                    </Text>
                                </PillButton>
                            </Row>
                        </Alert>
                    </Collapse>
                </Stack>

                <LocationTypes selectedTypes={locationTypes} setSelectedTypes={setLocationTypes} />

                <Row align="end" gap="sm" fullWidth>
                    <Tooltip
                        placement="top"
                        body="Enter the Program Number associated with the address of the location you entered above."
                    >
                        <IconTooltipTarget>
                            <Icon
                                icon={HelpOutlineIcon}
                                size="xs"
                                iconColor={Colors.secondary}
                                bgColor={Colors.secondarySoft}
                            />
                        </IconTooltipTarget>
                    </Tooltip>
                    <FieldGroup label={programLabel} style={{ flex: 1 }}>
                        <TextInput
                            id="txtProgramId"
                            value={programId ?? ''}
                            onChange={setProgramId}
                            error={!isValidProgramId ? 'Please enter a valid program ID.' : ''}
                            fullWidth
                        />
                    </FieldGroup>
                </Row>

                {programTypeOptions.length > 0 && (
                    <Stack gap="sm">
                        <Text>Do you currently participate in any of the following state programs?</Text>
                        <ProgramTypes
                            options={programTypeOptions.map((x) => x.name)}
                            programTypes={programTypes}
                            onProgramTypesChanged={onProgramTypesChanged}
                        />
                    </Stack>
                )}
            </Stack>
        </FormSection>
    );
}

const IconTooltipTarget = styled('span')({
    cursor: 'help',
    marginBottom: 8,
});
